import {
  DeviceDataType,
  _EventRequestDto as EventRequestDto,
  _EventType as EventType,
  _EventHelp,
} from '@vectronic/desktop-api';
export {
  _BaseEvent as BaseEvent,
  _EventRequestDto as EventRequestDto,
  _EventToDesktopDto as EventToDesktopDto,
  _EventToDesktopRequestDto as EventToDesktopRequestDto,
  _EventToHttpDto as EventToHttpDto,
  _EventToRequestDto as EventToRequestDto,
  _EventToWebDto as EventToWebDto,
  _EventType as EventType,
  _EventWebDto as EventWebDto,
  _WithPositionEvent as WithPositionEvent,
} from '@vectronic/desktop-api';

export type EventMod = 'CSV' | 'Count' | 'Page';

export class EventHelp extends _EventHelp {
  static setAnimalRequest(ids: number[], request: EventRequestDto) {
    const copy = JSON.parse(JSON.stringify(request));
    copy.animalIds = ids.filter(Boolean);
    delete copy.deviceIds;
    return copy;
  }

  static setDeviceRequest(ids: number[], request: EventRequestDto) {
    const copy = JSON.parse(JSON.stringify(request));
    copy.deviceIds = ids.filter(Boolean);
    delete copy.animalIds;
    return copy;
  }

  static statusEvents() {
    return [
      EventType.MORTALITY_IMPLANT,
      EventType.PROXIMITY,
      EventType.SEPARATION,
      EventType.VAGINAL_IMPLANT,
      EventType.VIRTUAL_FENCE,
      EventType.ACTIVITY,
    ] as const;
  }

  static override toString(event: EventType) {
    switch (event) {
      case EventType.MORTALITY:
        return 'Mortality';
      case EventType.MORTALITY_IMPLANT:
        return 'MortalityImplant';
      case EventType.PROXIMITY:
        return 'Proximity';
      case EventType.SEPARATION:
        return 'Separation';
      case EventType.TRAP:
        return 'Trap';
      case EventType.VAGINAL_IMPLANT:
        return 'VaginalImplant';
      case EventType.VIRTUAL_FENCE:
        return 'VirtualFence';
      case EventType.POSITION:
        return 'Position';
      case EventType.ACTIVITY:
        return 'Activities';
      case EventType.COLLAR_LOG:
        return 'CollarLog';
    }
  }

  static toPageTitle(event: EventType) {
    return `${EventHelp.toTitleString(event)}${event === EventType.ACTIVITY ? '' : ' Events'}`;
  }
  static toTitleString(event: EventType) {
    if (event === EventType.MORTALITY_IMPLANT) return 'Mortality Implant';
    if (event === EventType.VAGINAL_IMPLANT) return 'Vaginal Implant';
    if (event === EventType.VIRTUAL_FENCE) return 'Virtual Fence';
    if (event === EventType.COLLAR_LOG) return 'Log';
    return this.toString(event);
  }

  static toHyphenString(event: EventType) {
    if (event === EventType.MORTALITY_IMPLANT) return 'mortality-implant';
    if (event === EventType.VAGINAL_IMPLANT) return 'vaginal-implant';
    if (event === EventType.VIRTUAL_FENCE) return 'virtual-fence';
    if (event === EventType.ACTIVITY) return 'activities';
    if (event === EventType.POSITION) return 'positions';
    return this.toLowerCaseString(event);
  }

  static toLowerCaseString(event: EventType) {
    const str = this.toString(event);
    return str?.slice(0, 1).toLowerCase() + str?.slice(1);
  }

  static toKey<T extends EventType, K extends EventMod>(event: T, mod: K) {
    //DEL?
    return `${this.toLowerCaseString(event)}Events${mod === 'Page' ? '' : mod}` as const;
  }

  static filtersToDisplay(event: EventType) {
    const base = ['utc', 'lastXRecords', 'scts', 'origin', 'localData', 'downloadTs'];
    switch (event) {
      case EventType.MORTALITY:
        return [...base, 'mortalityType'];
      case EventType.COLLAR_LOG:
        return [...base, 'showSystemLog'];
      case EventType.MORTALITY_IMPLANT:
      case EventType.PROXIMITY:
        return [...base];
      case EventType.SEPARATION:
        return [...base, 'description'];
      case EventType.TRAP:
        return ['lastXRecords', 'utc-trap'];
      case EventType.VAGINAL_IMPLANT:
        return [...base, 'description'];
      case EventType.VIRTUAL_FENCE:
        return [...base, 'latitude', 'vfStatus'];
    }
    return [...base];
  }

  static toPageDetail(event: EventType): { title: string; path: string; tag: string } {
    switch (event) {
      case EventType.POSITION:
        return { title: 'Positions', path: 'positions', tag: 'POS' };
      case EventType.MORTALITY:
        return { title: 'Mortalities', path: 'mortalities', tag: 'MOR' };
      case EventType.MORTALITY_IMPLANT:
        return { title: 'Mortality Implants', path: 'mortality-implants', tag: 'MIT' };
      case EventType.SEPARATION:
        return { title: 'Separations', path: 'separations', tag: 'SEP' };
      case EventType.PROXIMITY:
        return { title: 'Proximities', path: 'proximities', tag: 'PRX' };
      case EventType.VAGINAL_IMPLANT:
        return { title: 'Vaginal Implants', path: 'vaginal-implants', tag: 'VIT' };
      case EventType.ACTIVITY:
        return { title: 'Activities', path: 'activities', tag: 'ACT' };
      case EventType.COLLAR_LOG:
        return { title: 'Collar Logs', path: 'collar-logs', tag: 'LOG' };
      case EventType.TRAP:
        return { title: 'Trap Events', path: 'trap-events', tag: 'TRAP' };
      case EventType.VIRTUAL_FENCE:
        return { title: 'Virtual Fence', path: 'virtual-fence-events', tag: 'VFE' };
      default:
        return { title: '', path: '', tag: '' };
    }
  }

  static fromDeviceData(deviceDataType: DeviceDataType) {
    switch (deviceDataType) {
      case DeviceDataType.MORTALITY_IMPLANT:
        return EventType.MORTALITY_IMPLANT;
      case DeviceDataType.PROXIMITY:
        return EventType.PROXIMITY;
      case DeviceDataType.SEPARATION:
        return EventType.SEPARATION;
      case DeviceDataType.VAGINAL_IMPLANT:
        return EventType.VAGINAL_IMPLANT;
      case DeviceDataType.POSITION:
        return EventType.POSITION;
      case DeviceDataType.ACTIVITY:
        return EventType.ACTIVITY;
      case DeviceDataType.COLLAR_LOGS:
        return EventType.COLLAR_LOG;
      case DeviceDataType.MORTALITY:
        return EventType.MORTALITY;
    }
  }
}
